import * as R from "ramda";
import React from "react";
import { FaTrash } from "react-icons/fa";
import * as RR from "react-redux";
import { Action } from "redux-actions";
import styled from "styled-components";
import { IOrganizationDisplayInfo, ResolvableType } from "../../../../api/ResolveApi/resolve-api.types";
import { IClientPatch, IPatchAction } from "../../../../components/course-author/helpers/patches";
import Checkbox from "../../../../components/pages/CourseAuthor/Checkbox";
import { api } from "../../../../dependencies";
import * as rootSelectors from "../../../../selectors/selectors";
import {
  ButtonRow,
  CheckboxDescription,
  DeleteButton,
  StyledSelect as CommonStyledSelect,
} from "../../../Common/Components/FormComponents";
import {
  SectionSubTitle as SectionSubTitleCommon,
  SectionTitle,
  SubSection,
  WarningSection,
} from "../../../Common/Styles/containers";
import { LMSMap, VersionMap } from "../../../ScormPackageManagement/utils";
import { ICompleteOrganization } from "../../../types/common";
import * as actions from "../actions";
import {
  changeLMSPatch,
  changeOwningOrganizationPatch,
  ChangePatch,
  changeReleaseLinePatch,
  changeSCORMVersionPatch,
  toggleHideLMSNavBarPatch,
  toggleLearnerLogPatch,
} from "../helpers/patches";
import { verifyRootOrgAdmin } from "../helpers/root";
import * as selectors from "../reducers/selectors";

const StyledSelect = styled(CommonStyledSelect)`
  background-color: ${({ theme }) => theme.colors.lightgrey3};
  border-radius: 0.3rem;
  width: 80%;
`;

const SectionSubTitle = styled(SectionSubTitleCommon)`
  margin: 1rem 0;
`;

const Note = styled.p`
  color: ${({ theme }) => theme.colors.lightsilver};
  font-style: italic;
  font-weight: bold;
`;

const MarginRightNote = styled(Note)`
  margin: 1rem 0 0 2.5rem;
`;

const LMSOptions = Object.keys(LMSMap).map(key => ({
  name: LMSMap[key],
  value: key,
}));

const SCORMVersions = Object.keys(VersionMap).map(key => ({
  name: VersionMap[key],
  value: key,
}));

interface ILoadingArgs {
  accountId: string;
  organizationId: string;
  setOwningOrgs: React.Dispatch<React.SetStateAction<IOrganizationDisplayInfo[]>>;
  setReleaseLines: React.Dispatch<React.SetStateAction<string[]>>;
}

const loadOwningOrganizations = async (args: ILoadingArgs) => {
  const [idsErr, idsRes] = await api.organizations.getUndeletedNonChildrenOf(args.organizationId, args.accountId);
  if (idsErr || !idsRes) {
    return;
  }
  const [resolveErr, resolveRes] = await api.resolve.displayInfo.post(
    args.accountId,
    idsRes.data.map(id => [ResolvableType.Organization, id])
  );
  if (resolveErr || !resolveRes) {
    return;
  }
  args.setOwningOrgs(
    R.filter(([_, resolvedData]) => !R.isNil(resolvedData), R.zip(idsRes.data, resolveRes.data)).map(
      ([organizationId, resolvedData]) => ({
        ...resolvedData.value,
        organizationId,
      })
    )
  );
};

const loadReleaseLines = async (args: ILoadingArgs) => {
  const [err, res] = await api.packages.getReleaseLineClientPackages(args.accountId);
  if (err || !res) {
    return;
  }
  args.setReleaseLines(res.data);
};

const loadInitialData = async (args: ILoadingArgs) => {
  await Promise.all([loadOwningOrganizations(args), loadReleaseLines(args)]);
};

interface IDangerZoneProps {
  accountId: string;
  organization: ICompleteOrganization;
  patchRequest: (patch: IClientPatch<IPatchAction>) => Action<IClientPatch<IPatchAction>>;
}

const DangerZoneOptions: React.FC<IDangerZoneProps> = ({ accountId, organization, patchRequest }) => {
  const [owningOrgs, setOwningOrgs] = React.useState<IOrganizationDisplayInfo[]>([]);
  const [releaseLines, setReleaseLines] = React.useState<string[]>([]);

  React.useEffect(() => {
    loadInitialData({
      accountId,
      organizationId: organization.organizationId,
      setOwningOrgs,
      setReleaseLines,
    });
  }, [organization.organizationId]);

  const scormSettings = organization.scormSettings;
  const handleChange = (currentValue: string, changePatch: ChangePatch) => (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = e.target.value;
    if (newValue !== currentValue) {
      patchRequest(changePatch(organization, accountId, newValue));
    }
  };
  const changeOwningOrganization = handleChange(organization.owner, changeOwningOrganizationPatch);
  const changeReleaseLine = handleChange(organization.releaseLine, changeReleaseLinePatch);
  const changeLMS = handleChange(scormSettings.lms, changeLMSPatch);
  const changeSCORMVersion = handleChange(scormSettings.scormVersion, changeSCORMVersionPatch);
  const toggleLearnerLog = () => {
    patchRequest(toggleLearnerLogPatch(organization, accountId));
  };
  const toggleHideLMSNavBar = () => {
    patchRequest(toggleHideLMSNavBarPatch(organization, accountId));
  };

  return (
    <WarningSection>
      <SectionTitle>Root Org Admin Options</SectionTitle>
      <SubSection>
        <SectionSubTitle>Owning Organisation</SectionSubTitle>
        <StyledSelect aria-label="owning-organization" onChange={changeOwningOrganization} value={organization.owner}>
          {owningOrgs.map(owningOrg => (
            <option key={owningOrg.organizationId} value={owningOrg.organizationId}>
              {owningOrg.displayName || "Unknown Organisation"}
            </option>
          ))}
        </StyledSelect>
      </SubSection>

      <SubSection>
        <label>
          <Checkbox defaultChecked={organization.storeLearnerLog} onChange={toggleLearnerLog} />
          <CheckboxDescription>Learner Log (EXPERIMENTAL) </CheckboxDescription>
          <MarginRightNote>
            Store the learner log for all users of this organisation. Only enable this for organisations with less than
            100 users.
          </MarginRightNote>
        </label>
      </SubSection>

      <SubSection>
        <SectionSubTitle>Release Line</SectionSubTitle>
        <StyledSelect aria-label="releaseLines" onChange={changeReleaseLine} value={organization.releaseLine}>
          {releaseLines.map((value, i) => (
            <option key={i} value={value}>
              {value}
            </option>
          ))}
        </StyledSelect>
      </SubSection>

      <SubSection>
        <SectionSubTitle>LMS</SectionSubTitle>
        <StyledSelect aria-label="LMS" onChange={changeLMS} value={scormSettings.lms}>
          {LMSOptions.map(({ value, name }, i) => (
            <option key={i} value={value}>
              {name}
            </option>
          ))}
        </StyledSelect>
      </SubSection>

      <SubSection>
        <SectionSubTitle>SCORM Version</SectionSubTitle>
        <StyledSelect aria-label="SCORMVersion" onChange={changeSCORMVersion} value={scormSettings.scormVersion}>
          {SCORMVersions.map(({ value, name }, i) => (
            <option key={i} value={value}>
              {name}
            </option>
          ))}
        </StyledSelect>
        <Note>
          Using SCORM 2004 3rd edition allows us to ensure that the progressing/resetting of progress and completion
          information is more complete.
        </Note>
        <Note>
          SCORM 1.2 is better supported among LMSes and robust. However, it is not as feature rich as SCORM 2004.
        </Note>
      </SubSection>

      <SubSection>
        <label>
          <Checkbox defaultChecked={scormSettings.hideNavBar} onChange={toggleHideLMSNavBar} />
          <CheckboxDescription>Hide LMS Navigation Bar</CheckboxDescription>
        </label>
      </SubSection>
    </WarningSection>
  );
};

const deleteOrganizationConfirmMsg = `Are you sure you want to delete this organisation?`;

interface IDeleteOrganizationProps {
  deleteOrganization: () => void;
}

const DeleteOrganization: React.FC<IDeleteOrganizationProps> = ({ deleteOrganization }) => {
  const toggleDelete = () => {
    if (confirm(deleteOrganizationConfirmMsg)) {
      deleteOrganization();
    }
  };
  const targetOrganizationId = RR.useSelector(selectors.currentCompleteOrganizationId);
  const isRootOrgAdmin = verifyRootOrgAdmin(targetOrganizationId || "");
  return (
    <div>
      <h2>Danger Zone</h2>
      <ButtonRow margin={1} width={18}>
        <DeleteButton disabled={isRootOrgAdmin} onClick={toggleDelete}>
          <FaTrash />
          Delete Organisation
        </DeleteButton>
      </ButtonRow>
      {isRootOrgAdmin && <p>You cannot delete this organisation because it's the root organisation.</p>}
    </div>
  );
};

const DangerZone: React.FC<{}> = () => {
  const dispatch = RR.useDispatch();
  const organization = RR.useSelector(selectors.currentCompleteOrganization);
  const accountId = RR.useSelector(rootSelectors.accountId);
  const administrableOrgIds = RR.useSelector(rootSelectors.administrableOrgIds);
  const patchRequest = (patch: IClientPatch<IPatchAction>) => dispatch(actions.patchRequest(patch));
  const deleteOrganization = () => dispatch(actions.deleteOrganization());

  if (!organization) {
    return <p>Looking for organization...</p>;
  }

  if (!R.any(verifyRootOrgAdmin)(administrableOrgIds)) {
    return null;
  }

  const props: IDangerZoneProps = {
    accountId,
    organization,
    patchRequest,
  };

  return (
    <div>
      <DangerZoneOptions {...props} />
      <DeleteOrganization deleteOrganization={deleteOrganization} />
    </div>
  );
};

export default DangerZone;
